<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import OptionSelect from 'dashboard/components-next/select/OptionSelect.vue';
import HolidayDatePicker from 'dashboard/routes/dashboard/settings/businessHours/components/HolidayDatePicker.vue';
import BusinessHoursTimePicker from 'dashboard/routes/dashboard/settings/businessHours/components/BusinessHoursTimePicker.vue';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';
import {
  DATE_TIME_WINDOW_OPERATORS,
  DATE_TIME_WINDOW_TYPES,
  getDefaultDateTimeWindowParams,
} from 'dashboard/routes/dashboard/settings/automation/dateTimeWindow';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  errorMessage: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const router = useRouter();
const { currentAccount } = useAccount();

const defaultTimezone = computed(
  () => currentAccount.value?.settings?.account_timezone || 'UTC'
);

const timezoneOptions = computed(() => timeZoneOptions());
const operatorOptions = computed(() =>
  DATE_TIME_WINDOW_OPERATORS.map(option => ({
    key: option.id,
    label: t(option.name),
  }))
);
const windowTypeOptions = computed(() =>
  DATE_TIME_WINDOW_TYPES.map(option => ({
    key: option.id,
    label: t(option.name),
  }))
);
const normalizedTimezoneOptions = computed(() =>
  timezoneOptions.value.map(option => ({
    key: option.value,
    label: option.label,
  }))
);
const accountBusinessHoursEnabled = computed(() =>
  Boolean(currentAccount.value?.working_hours_enabled)
);
const accountBusinessHoursTimezone = computed(
  () =>
    currentAccount.value?.settings?.account_timezone || defaultTimezone.value
);
const businessHoursSettingsRoute = computed(() => ({
  name: 'business_hours_settings',
  params: { accountId: currentAccount.value?.id },
}));
const businessHoursSettingsHref = computed(
  () => router.resolve(businessHoursSettingsRoute.value).href
);

const normalizedModel = computed(() => ({
  ...getDefaultDateTimeWindowParams(defaultTimezone.value),
  ...(props.modelValue || {}),
  timezone: props.modelValue?.timezone || defaultTimezone.value || 'UTC',
}));

const updateValue = patch => {
  emit('update:modelValue', {
    ...normalizedModel.value,
    ...patch,
  });
};

const timezoneModel = computed({
  get() {
    return normalizedModel.value.timezone;
  },
  set(value) {
    updateValue({ timezone: value });
  },
});

const operatorModel = computed({
  get() {
    return normalizedModel.value.operator;
  },
  set(value) {
    updateValue({ operator: value });
  },
});

const windowTypeModel = computed({
  get() {
    return normalizedModel.value.window_type;
  },
  set(value) {
    updateValue({
      window_type: value,
      timezone:
        value === 'business_hours'
          ? accountBusinessHoursTimezone.value
          : normalizedModel.value.timezone,
    });
  },
});

watch(
  defaultTimezone,
  timezone => {
    if (!props.modelValue?.timezone && timezone) {
      updateValue({ timezone });
    }
  },
  { immediate: true }
);

watch(
  accountBusinessHoursTimezone,
  timezone => {
    if (normalizedModel.value.window_type === 'business_hours' && timezone) {
      updateValue({ timezone });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-3">
      <div
        class="grid gap-3"
        :class="
          normalizedModel.window_type === 'absolute_range'
            ? 'grid-cols-2'
            : 'grid-cols-[minmax(0,2fr)_minmax(0,1fr)]'
        "
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium leading-4 text-n-slate-11">
            {{ $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.TYPE_LABEL') }}
          </label>
          <OptionSelect
            v-model="windowTypeModel"
            :options="windowTypeOptions"
            dropdown-max-height="max-h-56"
            :disabled="disabled"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium leading-4 text-n-slate-11">
            {{ $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.OPERATOR_LABEL') }}
          </label>
          <OptionSelect
            v-model="operatorModel"
            :options="operatorOptions"
            dropdown-max-height="max-h-56"
            :disabled="disabled"
          />
        </div>
      </div>

      <div
        v-if="normalizedModel.window_type === 'absolute_range'"
        class="flex flex-col gap-1"
      >
        <label class="text-xs font-medium leading-4 text-n-slate-11">
          {{ $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.TIMEZONE_LABEL') }}
        </label>
        <OptionSelect
          v-model="timezoneModel"
          :options="normalizedTimezoneOptions"
          dropdown-max-height="max-h-56"
          :disabled="disabled"
        />
      </div>
    </div>

    <div
      v-if="normalizedModel.window_type === 'absolute_range'"
      class="flex flex-col gap-3 px-1"
    >
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium leading-4 text-n-slate-11">
          {{ $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.START_LABEL') }}
        </label>
        <div class="grid grid-cols-[minmax(0,1fr)_8rem] gap-2">
          <HolidayDatePicker
            :model-value="normalizedModel.start_date"
            :placeholder="$t('AUTOMATION.FLOW.DATE_TIME_WINDOW.START_DATE')"
            @update:model-value="value => updateValue({ start_date: value })"
          />
          <BusinessHoursTimePicker
            :model-value="normalizedModel.start_time"
            :disabled="disabled"
            :has-error="Boolean(errorMessage)"
            @update:model-value="value => updateValue({ start_time: value })"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium leading-4 text-n-slate-11">
          {{ $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.END_LABEL') }}
        </label>
        <div class="grid grid-cols-[minmax(0,1fr)_8rem] gap-2">
          <HolidayDatePicker
            :model-value="normalizedModel.end_date"
            :placeholder="$t('AUTOMATION.FLOW.DATE_TIME_WINDOW.END_DATE')"
            @update:model-value="value => updateValue({ end_date: value })"
          />
          <BusinessHoursTimePicker
            :model-value="normalizedModel.end_time"
            :disabled="disabled"
            :has-error="Boolean(errorMessage)"
            @update:model-value="value => updateValue({ end_time: value })"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 dark:border-n-strong"
    >
      <p class="m-0 text-sm leading-5 text-n-slate-12">
        {{
          accountBusinessHoursEnabled
            ? $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.BUSINESS_HOURS_REFERENCE', {
                timezone: accountBusinessHoursTimezone,
              })
            : $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.BUSINESS_HOURS_DISABLED')
        }}
      </p>
      <a
        :href="businessHoursSettingsHref"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-2 inline-flex text-sm font-medium leading-5 text-n-brand hover:text-n-brand/80"
      >
        {{
          $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.BUSINESS_HOURS_SETTINGS_LINK')
        }}
      </a>
      <p class="mt-2 m-0 text-xs leading-4 text-n-slate-11">
        {{
          $t(
            'AUTOMATION.FLOW.DATE_TIME_WINDOW.BUSINESS_HOURS_WORKSPACE_ONLY_NOTE'
          )
        }}
      </p>
    </div>

    <p v-if="errorMessage" class="m-0 text-xs leading-4 text-n-ruby-11">
      {{ errorMessage }}
    </p>
  </div>
</template>
